import { totalProductWeightKg as totalWeightKg } from '../../lib/product-weight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { amountInWords, formatBoxPieces } from '@tiles-erp/shared';
import type { SalesInvoiceLineItem, SalesInvoicePrintData } from '@tiles-erp/shared-types';
import { LoadingOverlay } from '@tiles-erp/ui';
import { useClaimDeliverySlipPrint, useSalesInvoicePrint } from './invoices-api';
import { PrintLogo } from '../../app/branding';
import { useAuth } from '../../auth/AuthProvider';
import { PERMISSIONS } from '@tiles-erp/config';

/** A4/A5 for sheet copies, 110/80/58 mm for roll printers. */
type PaperSize = 'A4' | 'A5' | '110mm' | '80mm' | '58mm';
type InvoiceDocument = 'tax' | 'proforma' | 'delivery' | 'items';

const PAPER_LABELS: Record<PaperSize, string> = {
  A4: 'A4',
  A5: 'A5',
  '110mm': '110 mm roll',
  '80mm': '80 mm roll',
  '58mm': '58 mm roll',
};

const isPaperSize = (value: string | null): value is PaperSize =>
  value !== null && value in PAPER_LABELS;

const DOCUMENT_LABELS: Record<InvoiceDocument, string> = {
  tax: 'Tax invoice',
  proforma: 'Proforma invoice',
  delivery: 'Delivery slip',
  items: 'Item list',
};

const isInvoiceDocument = (value: string | null): value is InvoiceDocument =>
  value !== null && value in DOCUMENT_LABELS;

const money = (value: number): string =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const date = (value: string): string => new Date(value).toLocaleDateString('en-IN');

const quantity = (line: SalesInvoiceLineItem): string =>
  line.stockType === 'GRANITE' ? `${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft` : formatBoxPieces(line.qtyBoxes, line.piecesPerBox, line.baseUom === 'PIECE');

/** Keep piece-only products out of the BOX column on every print layout. */
const printedBoxes = (line: SalesInvoiceLineItem): number =>
  line.stockType === 'GRANITE' ? line.qtyBoxes : line.baseUom === 'PIECE' || line.piecesPerBox === 1 ? 0 : line.boxes;

const printedPieces = (line: SalesInvoiceLineItem): number =>
  line.stockType === 'GRANITE' ? 0 : line.baseUom === 'PIECE' || line.piecesPerBox === 1
    ? Math.round(line.qtyBoxes * Math.max(line.piecesPerBox, 1))
    : line.pieces;



const weight = (value: number): string =>
  value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pageStyle = (paper: PaperSize): string => {
  if (paper === 'A4') return `@page { size: A4 portrait; margin: 12mm 10mm; }`;
  if (paper === 'A5') return `@page { size: A5 portrait; margin: 5mm; }`;
  const rollWidth = paper === '110mm' ? '110mm' : paper === '80mm' ? '80mm' : '58mm';
  return `@page { size: ${rollWidth} auto; margin: 2mm; }`;
};

export function SalesInvoicePrintPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const requested = searchParams.get('paper');
  const requestedDocument = searchParams.get('document');
  const deliveryGodownId = searchParams.get('godownId');
  const canPrintDeliverySlip = hasPermission(PERMISSIONS.DELIVERY_SLIP_PRINT);
  const isGodownStaff = Boolean(user?.roles.includes('GODOWN STAFF'));
  const deliveryOnlyUser = Boolean(
    user?.roles.some((role) => role === 'GODOWN STAFF' || role === 'DELIVERY SLIP PRINT') &&
    !user.roles.some((role) => role !== 'GODOWN STAFF' && role !== 'DELIVERY SLIP PRINT'),
  );
  const requestedType = isInvoiceDocument(requestedDocument) ? requestedDocument : 'tax';
  const initialDocument = deliveryOnlyUser ? 'delivery' : requestedType;
  const [paper, setPaper] = useState<PaperSize>(
    isPaperSize(requested)
      ? requested
      : initialDocument === 'delivery'
        ? '110mm'
        : initialDocument === 'proforma'
          ? 'A5'
          : 'A4',
  );
  const [documentType, setDocumentType] = useState<InvoiceDocument>(initialDocument);
  const [deliveryPrinted, setDeliveryPrinted] = useState(false);
  const [deliveryClaimStarted, setDeliveryClaimStarted] = useState(false);
  const [deliveryPrintAuthorized, setDeliveryPrintAuthorized] = useState(false);
  const [claimedDeliveryData, setClaimedDeliveryData] = useState<SalesInvoicePrintData | null>(null);
  const invoicePrint = useSalesInvoicePrint(documentType === 'delivery' ? null : (id ?? null));
  const claimDeliveryPrint = useClaimDeliverySlipPrint();
  const data = documentType === 'delivery' ? claimedDeliveryData : invoicePrint.data;
  const isLoading = documentType !== 'delivery' && invoicePrint.isLoading;
  const isError = documentType !== 'delivery' && invoicePrint.isError;
  const error = invoicePrint.error;

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = pageStyle(paper);
    document.head.appendChild(style);
    return () => style.remove();
  }, [paper]);

  if (isLoading) return <LoadingOverlay open />;
  if (documentType !== 'delivery' && (isError || !data)) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'That invoice could not be loaded.'}
        </Alert>
      </Box>
    );
  }

  const isRoll = paper === '110mm' || paper === '80mm' || paper === '58mm';
  const sheetWidth = paper === 'A4' ? '190mm' : paper === 'A5' ? '138mm' : paper === '110mm' ? '102mm' : paper === '80mm' ? '72mm' : '50mm';
  const printDeliverySlips = (claimedData: SalesInvoicePrintData): void => {
    flushSync(() => {
      setClaimedDeliveryData(claimedData);
      setDeliveryPrintAuthorized(true);
    });
    // Each godown is a separate CSS page. Thermal drivers configured to cut after
    // every page therefore cut all the godown slips from this single print action.
    window.print();
    setDeliveryPrinted(true);
    setDeliveryPrintAuthorized(false);
  };

  return (
    <Box>
      <Stack
        className="print-hidden"
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {claimDeliveryPrint.isError && (
          <Alert severity="error" sx={{ mr: 1 }}>
            {claimDeliveryPrint.error instanceof Error
              ? claimDeliveryPrint.error.message
              : 'The delivery slip could not be printed.'}
          </Alert>
        )}
        {documentType === 'delivery' && isGodownStaff && !deliveryGodownId && (
          <Alert severity="warning" sx={{ mr: 1 }}>
            Open the godown slip from the Godown slips list. A godown was not selected.
          </Alert>
        )}
        <Button
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(deliveryOnlyUser ? '/delivery-slips' : '/sales-invoices')}
        >
          Back
        </Button>
        <TextField
          select
          label="Document"
          size="small"
          fullWidth={false}
          value={documentType}
          onChange={(e) => {
            const next = e.target.value as InvoiceDocument;
            setDocumentType(next);
            if (next === 'delivery') setPaper('110mm');
            if (next === 'proforma') setPaper('A5');
            if (next === 'tax') setPaper('A4');
            if (next === 'items') setPaper('A4');
          }}
          sx={{ width: 190 }}
        >
          {(Object.keys(DOCUMENT_LABELS) as InvoiceDocument[]).filter((type) =>
            deliveryOnlyUser
              ? type === 'delivery'
              : type !== 'delivery' || canPrintDeliverySlip,
          ).map((type) => (
            <MenuItem key={type} value={type}>
              {DOCUMENT_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Paper"
          size="small"
          fullWidth={false}
          value={paper}
          onChange={(e) => setPaper(e.target.value as PaperSize)}
          disabled={documentType === 'proforma'}
          sx={{ width: 170 }}
        >
          {(Object.keys(PAPER_LABELS) as PaperSize[]).map((size) => (
            <MenuItem key={size} value={size}>
              {PAPER_LABELS[size]}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          disabled={documentType === 'delivery' && (
            !canPrintDeliverySlip ||
            deliveryPrinted ||
            deliveryClaimStarted ||
            claimDeliveryPrint.isPending ||
            (isGodownStaff && !deliveryGodownId)
          )}
          onClick={() => {
            if (documentType !== 'delivery') {
              window.print();
              return;
            }
            if (
              !id ||
              deliveryPrinted ||
              deliveryClaimStarted ||
              claimDeliveryPrint.isPending ||
              (isGodownStaff && !deliveryGodownId)
            ) return;

            // Lock this page immediately. The API claim provides the permanent lock, so
            // returning to the invoice cannot obtain another copy from cached page data.
            setDeliveryClaimStarted(true);
            void claimDeliveryPrint.mutateAsync({ id, godownId: deliveryGodownId }).then((claimedData) => {
              if (!(claimedData.deliveryLines ?? claimedData.invoice.lines ?? []).length) {
                setDeliveryPrinted(true);
                return;
              }
              printDeliverySlips(claimedData);
            }).catch(() => {
              // Keep the button locked: an uncertain network response may still have
              // recorded the print claim. A fresh attempt asks the server for the truth.
              setDeliveryPrinted(true);
            }).finally(() => {
              setDeliveryClaimStarted(false);
            });
          }}
        >
          {documentType === 'delivery' && (claimDeliveryPrint.isPending || deliveryClaimStarted)
            ? 'Preparing…'
            : documentType === 'delivery' && !deliveryGodownId
              ? 'Print all godown slips'
              : 'Print'}
        </Button>
      </Stack>

      <style>{`
        .delivery-print-guard { display: none; }
        .inv-sheet {
          width: ${sheetWidth};
          box-sizing: border-box;
          margin: 12px auto;
          background: #fff;
          color: #000;
          font-family: Arial, Helvetica, sans-serif;
          font-variant-numeric: tabular-nums lining-nums;
          letter-spacing: 0.08px;
          font-size: ${isRoll ? (paper === '58mm' ? '10px' : '11px') : '12px'};
          line-height: 1.35;
          padding: ${isRoll ? '4px' : '0'};
        }
        .inv-sheet table { width: 100%; border-collapse: collapse; }
        .inv-sheet th, .inv-sheet td { padding: ${isRoll ? '1px 0' : '4px 6px'}; }
        .inv-sheet.paper-A5 { font-size: 9px; line-height: 1.2; }
        .inv-sheet.paper-A5 th, .inv-sheet.paper-A5 td { padding: 2px 3px; }
        .inv-sheet.paper-A5 .title { font-size: 14px; }
        .inv-sheet.paper-A5 .doc-title { padding: 3px 0; }
        .inv-sheet.paper-A5 .fine { font-size: 8px; }
        .inv-sheet .rule { border-top: 1px ${isRoll ? 'dashed' : 'solid'} #000; }
        .inv-sheet .print-item-grid > thead > tr > th,
        .inv-sheet .print-item-grid > tbody > tr > td {
          border: 1px solid #000;
        }
        .inv-sheet .num { text-align: right; white-space: nowrap; }
        .inv-sheet .muted { color: ${isRoll ? '#000' : '#555'}; }
        .inv-sheet .title {
          font-weight: 700;
          font-size: ${isRoll ? '13px' : '18px'};
        }
        .inv-sheet .doc-title {
          text-align: center;
          font-weight: 700;
          letter-spacing: 1px;
          padding: ${isRoll ? '2px 0' : '6px 0'};
        }
        .inv-sheet .invoice-number {
          font-size: 1.5em;
          line-height: 1.2;
          font-weight: 800;
          letter-spacing: .25px;
          overflow-wrap: anywhere;
        }
        .inv-sheet .fine { font-size: ${isRoll ? '9px' : '10px'}; }
        .inv-sheet .item-list-sheet { font-size: 14px; }
        .inv-sheet .item-list-sheet .title { font-size: 20px; }
        .inv-sheet.paper-A5 .item-list-sheet { font-size: 11px; }
        .inv-sheet.paper-A5 .item-list-sheet .title { font-size: 16px; }
        .inv-sheet.paper-A4 .item-list-sheet,
        .inv-sheet.paper-A5 .item-list-sheet {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
        }
        .inv-sheet.paper-A4 .item-list-sheet { height: 273mm; }
        .inv-sheet.paper-A5 .item-list-sheet {
          height: 200mm;
        }
        .inv-sheet .item-list-footer { margin-top: auto; }
        .inv-sheet .item-list-bottom-line {
          min-height: 28mm;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
        }
        .inv-sheet .godown-slip {
          display: block;
          width: 100%;
          break-inside: avoid;
          page-break-inside: avoid;
          break-after: page;
          page-break-after: always;
        }
        .inv-sheet .godown-slip:last-child {
          break-after: auto;
          page-break-after: auto;
        }
        .inv-sheet .delivery-slip {
          box-sizing: border-box;
          max-width: 100%;
          font-family: Arial, Helvetica, sans-serif;
          font-size: ${isRoll ? (paper === '58mm' ? '11px' : '12px') : '13px'};
          line-height: 1.3;
          font-weight: 700;
          color: #000;
          opacity: 1;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .inv-sheet .delivery-slip * {
          color: #000;
          opacity: 1;
          font-weight: 700;
        }
        .inv-sheet .delivery-slip th,
        .inv-sheet .delivery-slip td { font-weight: 700; }
        .inv-sheet .delivery-slip .print-item-grid {
          width: 100%;
          max-width: 100%;
          table-layout: fixed;
          box-sizing: border-box;
        }
        .inv-sheet .delivery-slip .print-item-grid th,
        .inv-sheet .delivery-slip .print-item-grid td {
          box-sizing: border-box;
          overflow: hidden;
          overflow-wrap: anywhere;
        }
        .inv-sheet .delivery-slip .rule { border-top: 1px solid #000; }
        .inv-sheet .delivery-slip .doc-title { font-size: ${isRoll ? '14px' : '16px'}; font-weight: 700; }
        .inv-sheet .proforma-sheet {
          color: #000;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 9px;
          line-height: 1.18;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .inv-sheet .proforma-sheet table { table-layout: fixed; }
        .inv-sheet .proforma-sheet th,
        .inv-sheet .proforma-sheet td {
          border: 1px solid #000;
          padding: 2px 4px;
          color: #000;
        }
        .inv-sheet .proforma-sheet .proforma-title {
          background: #000;
          color: #fff;
          font-weight: 700;
          text-align: center;
          letter-spacing: .5px;
          padding: 3px;
        }
        .inv-sheet .proforma-sheet .no-border,
        .inv-sheet .proforma-sheet .no-border td { border: 0; }
        .inv-sheet .proforma-sheet .item-grid thead { display: table-header-group; }
        .inv-sheet .proforma-sheet .item-grid tr { break-inside: avoid; page-break-inside: avoid; }
        @media print {
          html, body { width: 100%; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden !important; }
          .inv-sheet, .inv-sheet * { visibility: visible !important; }
          .inv-sheet.delivery-print-locked,
          .inv-sheet.delivery-print-locked * { display: none !important; }
          .inv-sheet.delivery-single-godown .godown-slip { display: none !important; }
          .inv-sheet.delivery-single-godown .godown-slip.print-selected { display: block !important; }
          .delivery-print-guard.active {
            display: block !important;
            visibility: visible !important;
            padding: 12mm;
            color: #000;
            font: 700 14px Arial, sans-serif;
          }
          .print-hidden, .print-hidden * { display: none !important; }
          .inv-sheet {
            position: absolute;
            left: 0;
            top: 0;
            width: ${sheetWidth};
            max-width: ${sheetWidth};
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body { background: #fff; }
        }
      `}</style>

      <Box
        className={`delivery-print-guard ${documentType === 'delivery' && !deliveryPrintAuthorized ? 'active' : ''}`}
      >
        Delivery slip printing is locked. Use the Print button in ERP to issue the one permitted copy.
      </Box>

      <Box
        className={`inv-sheet paper-${paper}${documentType === 'delivery' && !deliveryPrintAuthorized ? ' delivery-print-locked' : ''}${documentType === 'delivery' && deliveryGodownId ? ' delivery-single-godown' : ''}`}
      >
        {data ? (
          <PrintBody
            data={data}
            isRoll={isRoll}
            documentType={documentType}
            isGodownCopy={Boolean(deliveryGodownId)}
            deliveryGodownId={deliveryGodownId}
          />
        ) : (
          <Alert severity="info" className="print-hidden">
            Press Print to issue the one permitted delivery-slip copy. The slip is loaded only after the server locks it.
          </Alert>
        )}
      </Box>
    </Box>
  );
}

/** The tax invoice itself: one column on a roll, a full GST layout on A4. */
function PrintBody({
  data,
  isRoll,
  documentType,
  isGodownCopy,
  deliveryGodownId,
}: {
  data: SalesInvoicePrintData;
  isRoll: boolean;
  documentType: InvoiceDocument;
  isGodownCopy: boolean;
  deliveryGodownId: string | null;
}): JSX.Element {
  const { invoice, company, branch, terms, declaration, customerPincode } = data;
  const lines = invoice.lines ?? [];
  const totalWeight = totalWeightKg(lines);
  const totalSummaryRows = 1 + (invoice.isInterState ? 1 : 2) + 3 + (invoice.roundOff !== 0 ? 1 : 0) + 1;

  if (documentType === 'delivery') {
    return <DeliverySlip data={data} isRoll={isRoll} isGodownCopy={isGodownCopy} printGodownId={deliveryGodownId} />;
  }

  if (documentType === 'proforma' && !isRoll) {
    return <ProformaInvoice data={data} />;
  }

  if (documentType === 'items') {
    return <InvoiceItemList data={data} />;
  }

  const documentTitle = documentType === 'proforma' ? 'PROFORMA INVOICE' : 'TAX INVOICE';

  return (
    <>
      <div style={{ textAlign: 'center' }}>
        <PrintLogo />
        <div className="title">{company.legalName ?? company.name}</div>
        {branch.addressLines.map((line) => (
          <div key={line}>{line}</div>
        ))}
        <div>{[branch.phone, branch.email].filter(Boolean).join('  ·  ')}</div>
        {(branch.gstin ?? company.gstin) && (
          <div>
            <strong>GSTIN {branch.gstin ?? company.gstin}</strong>
          </div>
        )}
      </div>

      <div className="rule doc-title">
        {documentTitle}
        {invoice.status === 'CANCELLED' ? ' — CANCELLED' : ''}
      </div>

      {isRoll ? (
        <div>
          <div>
            <strong>No:</strong> <span className="invoice-number">{invoice.invoiceNumber}</span>
          </div>
          <div>
            <strong>Date:</strong> {date(invoice.invoiceDate)}
          </div>
          <div className="rule" />
          <div>
            <strong>{invoice.customerName}</strong>
          </div>
          {invoice.customerAddress && <div>{invoice.customerAddress}</div>}
          <div>Pincode: {customerPincode || '-'}</div>
          {invoice.customerMobile && <div>Mob: {invoice.customerMobile}</div>}
          {invoice.customerGstin && <div>GSTIN: {invoice.customerGstin}</div>}
        </div>
      ) : (
        <table className="rule">
          <tbody>
            <tr>
              <td style={{ width: '55%', verticalAlign: 'top' }}>
                <div className="muted">Billed to</div>
                <div>
                  <strong>{invoice.customerName}</strong>
                </div>
                {invoice.customerAddress && <div>{invoice.customerAddress}</div>}
                <div>Pincode: {customerPincode || '-'}</div>
                {invoice.customerMobile && <div>Mobile: {invoice.customerMobile}</div>}
                {invoice.customerGstin && <div>GSTIN: {invoice.customerGstin}</div>}
              </td>
              <td style={{ verticalAlign: 'top' }}>
                <table>
                  <tbody>
                    <tr>
                      <td className="muted">Invoice no</td>
                      <td>
                        <strong className="invoice-number">{invoice.invoiceNumber}</strong>
                      </td>
                    </tr>
                    <tr>
                      <td className="muted">Date</td>
                      <td>{date(invoice.invoiceDate)}</td>
                    </tr>
                    {invoice.dueDate && (
                      <tr>
                        <td className="muted">Due date</td>
                        <td>{date(invoice.dueDate)}</td>
                      </tr>
                    )}
                    {invoice.orderNumber && (
                      <tr>
                        <td className="muted">Order</td>
                        <td>{invoice.orderNumber}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="muted">Place of supply</td>
                      <td>{invoice.placeOfSupply ?? '—'}</td>
                    </tr>
                    {invoice.salesmanName && (
                      <tr>
                        <td className="muted">Salesman</td>
                        <td>{invoice.salesmanName}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      <table className="print-item-grid" style={{ marginTop: isRoll ? 2 : 8 }}>
        <thead>
          <tr className="rule">
            {!isRoll && <th style={{ width: 26, textAlign: 'left' }}>#</th>}
            <th style={{ textAlign: 'left' }}>Item</th>
            {!isRoll && <th style={{ width: 80 }}>HSN</th>}
            {!isRoll && (
              <th className="num" style={{ width: 100 }}>
                Qty
              </th>
            )}
            {!isRoll && (
              <th className="num" style={{ width: 85 }}>
                Rate
              </th>
            )}
            {!isRoll && (
              <th className="num" style={{ width: 60 }}>
                GST%
              </th>
            )}
            {!isRoll && (
              <th className="num" style={{ width: 95 }}>
                Amount
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) =>
            isRoll ? (
              <tr key={line.id}>
                <td>
                  <div>
                    {index + 1}. {line.productName}{line.stockType === 'GRANITE' ? ` — ${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft (rate / sqft)` : ''}
                    {line.sizeMm ? ` (${line.sizeMm})` : ''}
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <td>{quantity(line)}</td>
                        <td className="num">x {money(line.rate)}</td>
                        <td className="num">
                          <strong>{money(line.lineTotal)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            ) : (
              <tr key={line.id} className="rule">
                <td>{index + 1}</td>
                <td>
                  {line.productName}{line.stockType === 'GRANITE' ? ` — ${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft (rate / sqft)` : ''}
                  {line.sizeMm && <span className="muted"> · {line.sizeMm}</span>}
                  {line.batchNo && <span className="muted"> · {line.batchNo}</span>}
                </td>
                <td>{line.hsnCode ?? '—'}</td>
                <td className="num">{quantity(line)}</td>
                <td className="num">{money(line.rate)}</td>
                <td className="num">{line.gstRate}</td>
                <td className="num">{money(line.lineTotal)}</td>
              </tr>
            ),
          )}
        </tbody>
      </table>

      <table className="rule" style={{ marginTop: isRoll ? 2 : 6 }}>
        <tbody>
          {!isRoll && (
            <tr>
              <td rowSpan={totalSummaryRows} style={{ verticalAlign: 'top', width: '55%' }}>
                <div className="muted">Amount in words</div>
                <div>
                  <strong>{amountInWords(invoice.grandTotal)}</strong>
                </div>
                {invoice.remarks && (
                  <div style={{ marginTop: 6 }}>
                    <span className="muted">Remarks: </span>
                    {invoice.remarks}
                  </div>
                )}
              </td>
              <td className="muted">Taxable value</td>
              <td className="num">{money(invoice.subTotal)}</td>
            </tr>
          )}
          {isRoll && (
            <tr>
              <td className="muted">Taxable value</td>
              <td className="num">{money(invoice.subTotal)}</td>
            </tr>
          )}
          {invoice.isInterState ? (
            <tr>
              <td className="muted">IGST</td>
              <td className="num">{money(invoice.igstAmount)}</td>
            </tr>
          ) : (
            <>
              <tr>
                <td className="muted">CGST</td>
                <td className="num">{money(invoice.cgstAmount)}</td>
              </tr>
              <tr>
                <td className="muted">SGST</td>
                <td className="num">{money(invoice.sgstAmount)}</td>
              </tr>
            </>
          )}
          <tr>
            <td className="muted">Freight charges</td>
            <td className="num">{money(invoice.freightCharge)}</td>
          </tr>
          <tr>
            <td className="muted">Loading charges</td>
            <td className="num">{money(invoice.loadingCharge)}</td>
          </tr>
          <tr>
            <td className="muted">Unloading charges</td>
            <td className="num">{money(invoice.unloadingCharge)}</td>
          </tr>
          {invoice.roundOff !== 0 && (
            <tr>
              <td className="muted">Round off</td>
              <td className="num">{money(invoice.roundOff)}</td>
            </tr>
          )}
          <tr className="rule">
            <td>
              <strong>Total</strong>
            </td>
            <td className="num">
              <strong>{money(invoice.grandTotal)}</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="rule" style={{ marginTop: 4 }}>
        <strong>Total product weight: {weight(totalWeight)} kg</strong>
      </div>

      {isRoll && (
        <div style={{ marginTop: 2 }}>
          <div className="rule" />
          <div>{amountInWords(invoice.grandTotal)}</div>
          {invoice.salesmanName && <div>Salesman: {invoice.salesmanName}</div>}
        </div>
      )}

      {terms.length > 0 && (
        <div className="fine" style={{ marginTop: isRoll ? 4 : 10 }}>
          <div className="rule" />
          <div>
            <strong>Terms &amp; conditions</strong>
          </div>
          <ol style={{ margin: '2px 0 0 16px', padding: 0 }}>
            {terms.map((term) => (
              <li key={term}>{term}</li>
            ))}
          </ol>
        </div>
      )}

      {!isRoll && declaration && (
        <div className="fine" style={{ marginTop: 8 }}>
          <strong>Declaration:</strong> {declaration}
        </div>
      )}

      <div style={{ marginTop: isRoll ? 6 : 20, textAlign: isRoll ? 'center' : 'right' }}>
        {isRoll ? (
          <div>Thank you for your business</div>
        ) : (
          <>
            <div>For {company.legalName ?? company.name}</div>
            <div style={{ marginTop: 28 }} className="muted">
              Authorised signatory
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Warehouse-friendly invoice item list without prices or tax values. */
function InvoiceItemList({ data }: { data: SalesInvoicePrintData }): JSX.Element {
  const { invoice, company, branch, customerPincode } = data;
  const lines = invoice.lines ?? [];
  const totalBoxes = lines.reduce((sum, line) => sum + printedBoxes(line), 0);
  const totalPieces = lines.reduce((sum, line) => sum + printedPieces(line), 0);
  const totalWeight = totalWeightKg(lines);
  return (
    <div className="item-list-sheet">
      <div style={{ textAlign: 'center' }}>
        <PrintLogo />
        <div className="title">{company.legalName ?? company.name}</div>
        <div>{branch.name}</div>
      </div>
      <div className="rule doc-title">SALES INVOICE ITEM LIST</div>
      <table>
        <tbody>
          <tr>
            <td><strong>Invoice:</strong> <span className="invoice-number">{invoice.invoiceNumber}</span></td>
            <td><strong>Date:</strong> {date(invoice.invoiceDate)}</td>
          </tr>
          <tr>
            <td><strong>Customer:</strong> {invoice.customerName} · <strong>Pincode:</strong> {customerPincode || '-'}</td>
            <td><strong>Salesman:</strong> {invoice.salesmanName ?? '—'}</td>
          </tr>
          <tr>
            <td>
              <div><strong>Address:</strong> {invoice.customerAddress || '—'}</div>
              <div><strong>Phone:</strong> {invoice.customerMobile || '—'}</div>
            </td>
            <td />
          </tr>
        </tbody>
      </table>
      <table className="print-item-grid" style={{ marginTop: 8 }}>
        <thead>
          <tr className="rule">
            <th style={{ width: 28, textAlign: 'left' }}>#</th>
            <th style={{ textAlign: 'left' }}>Item</th>
            <th style={{ width: 95, textAlign: 'left' }}>Size</th>
            <th className="num" style={{ width: 55 }}>Box</th>
            <th className="num" style={{ width: 55 }}>Pcs</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id} className="rule">
              <td>{index + 1}</td>
              <td>{line.productName}{line.stockType === 'GRANITE' ? ` — ${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft (rate / sqft)` : ''}</td>
              <td>{line.sizeMm ?? '—'}</td>
              <td className="num">{printedBoxes(line)}</td>
              <td className="num">{printedPieces(line)}</td>
            </tr>
          ))}
          <tr className="rule">
            <td colSpan={3}><strong>Total items: {lines.length}</strong></td>
            <td className="num"><strong>{totalBoxes}</strong></td>
            <td className="num"><strong>{totalPieces}</strong></td>
          </tr>
        </tbody>
      </table>
      <div className="item-list-footer">
        {invoice.remarks && <div style={{ marginTop: 8 }}><strong>Remarks:</strong> {invoice.remarks}</div>}
        <div className="item-list-bottom-line">
          <strong>Total product weight: {weight(totalWeight)} kg</strong>
          <strong>Customer signature</strong>
        </div>
      </div>
    </div>
  );
}

/** A5 proforma copy matching the compact counter format used by Avthar Ceramics. */
function ProformaInvoice({ data }: { data: SalesInvoicePrintData }): JSX.Element {
  const { invoice, company, branch, terms, customerPincode } = data;
  const lines = invoice.lines ?? [];
  const totalBoxes = lines.reduce((sum, line) => sum + printedBoxes(line), 0);
  const totalPieces = lines.reduce((sum, line) => sum + printedPieces(line), 0);
  const itemTotal = lines.reduce((sum, line) => sum + line.lineSubTotal, 0);
  const totalWeight = totalWeightKg(lines);
  const emptyRows = Array.from({ length: Math.max(0, 17 - lines.length) });
  const companyName = company.legalName ?? company.name;
  const gstin = branch.gstin ?? company.gstin;
  const invoiceDate = new Date(invoice.invoiceDate);
  const formattedDate = [
    String(invoiceDate.getDate()).padStart(2, '0'),
    String(invoiceDate.getMonth() + 1).padStart(2, '0'),
    invoiceDate.getFullYear(),
  ].join('-');

  return (
    <div className="proforma-sheet">
      <div style={{ textAlign: 'right', fontSize: 7, marginBottom: 2 }}>
        ORIGINAL / DUPLICATE / TRIPLICATE
      </div>

      <div style={{ border: '1px solid #000', padding: '7px 4px' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{companyName}</div>
        <div style={{ marginTop: 3 }}>{branch.addressLines.join(', ')}</div>
        {branch.phone && <div>PHONE : {branch.phone}</div>}
        {gstin && <div>GST No : {gstin}</div>}
      </div>

      <div className="proforma-title">PROFORMA INVOICE</div>

      <table>
        <tbody>
          <tr>
            <td style={{ width: '58%', height: 76, verticalAlign: 'top' }}>
              <strong>To</strong>
              <div style={{ marginTop: 8 }}>{invoice.customerName}</div>
              {invoice.customerAddress && <div>{invoice.customerAddress}</div>}
              <div>Pincode : {customerPincode || '-'}</div>
              <div>Phone : {invoice.customerMobile || '-'}</div>
            </td>
            <td style={{ padding: 0, verticalAlign: 'top' }}>
              <table className="no-border">
                <tbody>
                  <tr><td><strong>Order No</strong></td><td>: <span className="invoice-number">{invoice.invoiceNumber}</span></td></tr>
                  <tr><td><strong>Order Date</strong></td><td>: {formattedDate}</td></tr>
                  <tr><td><strong>SalesMan</strong></td><td>: {invoice.salesmanName || '-'}</td></tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <table className="item-grid">
        <thead>
          <tr>
            <th style={{ width: 18 }}>S/N</th>
            <th style={{ textAlign: 'center' }}>Description</th>
            <th className="num" style={{ width: 32 }}>BOX</th>
            <th className="num" style={{ width: 32 }}>PCS</th>
            <th className="num" style={{ width: 48 }}>Rate</th>
            <th className="num" style={{ width: 62 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={line.id} style={{ height: 13 }}>
              <td style={{ textAlign: 'center' }}>{index + 1}</td>
              <td>{line.productName}{line.stockType === 'GRANITE' ? ` — ${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft (rate / sqft)` : ''}{line.sizeMm ? ` (${line.sizeMm})` : ''}</td>
              <td className="num">{printedBoxes(line)}</td>
              <td className="num">{printedPieces(line)}</td>
              <td className="num">{money(line.rate)}</td>
              <td className="num">{money(line.lineSubTotal)}</td>
            </tr>
          ))}
          {emptyRows.map((_, index) => (
            <tr key={`blank-${index}`} aria-hidden="true" style={{ height: 13 }}>
              <td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td>
            </tr>
          ))}
          <tr>
            <td colSpan={2}><strong>Total Items : {lines.length}</strong></td>
            <td className="num"><strong>{totalBoxes}</strong></td>
            <td className="num"><strong>{totalPieces}</strong></td>
            <td className="num"><strong>Total :</strong></td>
            <td className="num"><strong>{money(itemTotal)}</strong></td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 3 }}><strong>Total product weight: {weight(totalWeight)} kg</strong></div>

      <table>
        <tbody>
          <tr>
            <td style={{ width: '48%', height: 94, verticalAlign: 'top' }}>
              <strong>{amountInWords(invoice.grandTotal).toUpperCase()}</strong>
              {invoice.remarks && <div style={{ marginTop: 8 }}><strong>Remarks:</strong> {invoice.remarks}</div>}
            </td>
            <td style={{ padding: 0, verticalAlign: 'top' }}>
              <table>
                <tbody>
                  <tr><td>ADD : AUTO FREIGHT</td><td className="num">{money(invoice.freightCharge)}</td></tr>
                  <tr><td>ADD : UNLOADING</td><td className="num">{money(invoice.unloadingCharge)}</td></tr>
                  <tr><td>ADD : LOADING CHARGE</td><td className="num">{money(invoice.loadingCharge)}</td></tr>
                  {invoice.isInterState ? (
                    <tr><td>TOTAL IGST</td><td className="num">{money(invoice.igstAmount)}</td></tr>
                  ) : (
                    <>
                      <tr><td>TOTAL CGST</td><td className="num">{money(invoice.cgstAmount)}</td></tr>
                      <tr><td>TOTAL SGST</td><td className="num">{money(invoice.sgstAmount)}</td></tr>
                    </>
                  )}
                  <tr><td>ADD/LESS : Round Off</td><td className="num">{money(invoice.roundOff)}</td></tr>
                  <tr><td><strong>NET AMOUNT</strong></td><td className="num"><strong>{money(invoice.grandTotal)}</strong></td></tr>
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>

      <table>
        <tbody>
          <tr>
            <td style={{ width: '58%', height: 53, verticalAlign: 'top', fontSize: 7.5 }}>
              {terms.map((term) => <div key={term}>{term}</div>)}
            </td>
            <td style={{ textAlign: 'center', verticalAlign: 'top' }}>
              <strong>For {companyName}</strong>
              <div style={{ marginTop: 30 }}><strong>Authorised Signatory</strong></div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** A delivery copy deliberately excludes rates, tax values and invoice totals. */
function DeliverySlip({
  data,
  isRoll,
  isGodownCopy,
  printGodownId,
}: {
  data: SalesInvoicePrintData;
  isRoll: boolean;
  isGodownCopy: boolean;
  printGodownId: string | null;
}): JSX.Element {
  const { invoice, company, branch, customerPincode } = data;
  // Use the physical picking rows for both copies. Cross-branch billing can move stock
  // into the invoice branch first, so invoice.lines may name only the destination godown;
  // deliveryLines preserves every godown that physically supplies the goods.
  const lines = data.deliveryLines ?? invoice.lines ?? [];
  const slips = Array.from(
    lines.reduce((groups, line) => {
      const group = groups.get(line.godownId) ?? { name: line.godownName, lines: [] as SalesInvoiceLineItem[] };
      group.lines.push(line);
      groups.set(line.godownId, group);
      return groups;
    }, new Map<string, { name: string; lines: SalesInvoiceLineItem[] }>()),
  ).map(([id, group]) => ({ id, ...group }));

  return (
    <>
      {slips.map((godown) => {
        const totalBoxes = godown.lines.reduce((sum, line) => sum + printedBoxes(line), 0);
        const totalPieces = godown.lines.reduce((sum, line) => sum + printedPieces(line), 0);
        const totalWeight = totalWeightKg(godown.lines);
        return (
          <div
            className={`godown-slip delivery-slip${godown.id === printGodownId ? ' print-selected' : ''}`}
            data-godown-id={godown.id}
            key={godown.id}
            style={{ padding: isRoll ? '1px' : 0 }}
          >
            <div className="doc-title" style={{ borderBottom: '1px solid #000' }}>
              {isGodownCopy ? 'GODOWN SLIP' : 'DELIVERY SLIP'}
            </div>
            {isRoll ? (
              <div aria-label={`Barcode ${invoice.invoiceNumber}`} style={{ margin: '3px 8px', height: 26, border: '1px solid #000', background: 'repeating-linear-gradient(90deg,#000 0,#000 2px,#fff 2px,#fff 4px,#000 4px,#000 5px,#fff 5px,#fff 8px)' }} />
            ) : (
              <div style={{ textAlign: 'center' }}><PrintLogo /><div className="title">{company.legalName ?? company.name}</div></div>
            )}
            <div style={{ textAlign: 'center', fontWeight: 700, padding: '2px 0' }}>
              {godown.name.toUpperCase()}
            </div>
            {!isRoll && <div style={{ textAlign: 'center' }}>{branch.addressLines.join(' · ')}</div>}
            <div style={{ marginTop: 5 }}><strong>Customer :</strong> {invoice.customerName}</div>
            {invoice.customerAddress && <div><strong>Address :</strong> {invoice.customerAddress}</div>}
            <div><strong>Pincode :</strong> {customerPincode || '-'}</div>
            {invoice.customerMobile && <div><strong>Phone :</strong> {invoice.customerMobile}</div>}
            <div><strong>Order No :</strong> <span className="invoice-number">{invoice.invoiceNumber}</span></div>
            <div><strong>Order Date :</strong> {date(invoice.invoiceDate)}</div>
            {invoice.salesmanName && <div><strong>SalesMan :</strong> {invoice.salesmanName}</div>}
            <table className="print-item-grid" style={{ marginTop: 5, border: '1px solid #000' }}>
              <colgroup>
                <col style={{ width: '6%' }} />
                <col style={{ width: '62%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '9%' }} />
              </colgroup>
              <thead><tr>
                <th style={{ borderRight: '1px solid #000' }}>#</th>
                <th style={{ textAlign: 'left', borderRight: '1px solid #000' }}>Description</th>
                <th style={{ borderRight: '1px solid #000' }}>Size</th>
                <th className="num" style={{ borderRight: '1px solid #000' }}>BOX</th>
                <th className="num">Pcs</th>
              </tr></thead>
              <tbody>
                {godown.lines.map((line, index) => (
                  <tr key={line.id} className="rule">
                    <td>{index + 1}</td><td><strong>{line.productName}{line.stockType === 'GRANITE' ? ` — ${line.qtyBoxes} slabs / ${line.qtySqft ?? 0} sqft (rate / sqft)` : ''}</strong></td>
                    <td>{line.sizeMm ?? '—'}</td><td className="num">{printedBoxes(line)}</td><td className="num">{printedPieces(line)}</td>
                  </tr>
                ))}
                <tr className="rule"><td colSpan={3} className="num"><strong>Total</strong></td><td className="num"><strong>{totalBoxes}</strong></td><td className="num"><strong>{totalPieces}</strong></td></tr>
              </tbody>
            </table>
            <div style={{ marginTop: 4 }}><strong>Total product weight: {weight(totalWeight)} kg</strong></div>
            {invoice.remarks && <div style={{ marginTop: 4 }}><strong>Remarks:</strong> {invoice.remarks}</div>}
            {!isRoll && <div style={{ marginTop: 42, textAlign: 'right' }}>Received by customer</div>}
          </div>
        );
      })}
    </>
  );
}
